import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { Link, Navigate } from "react-router-dom";
import StatusPill from "../../components/StatusPill";
import { fmtDT } from "../../lib/constants";
import { toast } from "sonner";
import { QrCode, Camera, CheckCircle } from "@phosphor-icons/react";
import CameraEvidenceCapture from "../../components/evidence/CameraEvidenceCapture";
import { HandledHistoryPanel } from "../../components/HandledHistoryPanel";
import { CargoAlert } from "../../components/CargoAlert";

// P4 — controlled observed-package-type vocabulary (value → human-readable label).
const OBSERVED_PACKAGE_TYPES = [
  { value: "CARTON_BOX", label: "Carton / Box" },
  { value: "BAG_SACK", label: "Bag / Sack" },
  { value: "CRATE", label: "Crate" },
  { value: "BUNDLE", label: "Bundle" },
  { value: "ROLL_TUBE", label: "Roll / Tube" },
  { value: "OTHER", label: "Other" },
];

export default function RiderHome() {
  const { profile } = useAuth();
  const [shipments, setShipments] = useState([]);
  const [active, setActive] = useState(null);
  const [detail, setDetail] = useState(null);
  const [manualToken, setManualToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // M7 — physical Authority-Letter custody (Rider collection of the original from the Sender).
  const [custody, setCustody] = useState(null);
  const [collecting, setCollecting] = useState(false);
  const [physInvoice, setPhysInvoice] = useState(false);  // M10A Inc-7 — physical invoice copy/copies collected
  const [activeBlock, setActiveBlock] = useState(null);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);

  const refresh = () =>
    api.get("/rider/shipments")
      .then((r) => setShipments(r.data.shipments || []))
      .catch((e) => toast.error(e.displayMessage));

  const loadDetail = useCallback(async () => {
    if (!active) return;
    try {
      const { data } = await api.get(`/rider/shipments/${active.id}`);
      setDetail(data);
    } catch (e) {
      toast.error(e.displayMessage || "Failed to load");
    }
    try {
      const { data: c } = await api.get(`/booking/${active.id}/authority-letter/custody/state`);
      setCustody(c);
    } catch (e) { setCustody(null); }
  }, [active]);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  useEffect(() => {
    if (!active) { setDetail(null); return; }
    loadDetail();
  }, [active, loadDetail]);

  useEffect(() => {
    if (!active?.id) { setActiveBlock(null); return; }
    api.get(`/shipments/${active.id}/blocks`)
      .then((r) => setActiveBlock(r.data?.active || null))
      .catch(() => setActiveBlock(null));
  }, [active, detail]);

  if (!profile) return null;
  if (profile.role !== "RID") return <Navigate to="/" replace />;


  const startScanner = async () => {
    const { Html5Qrcode } = await import("html5-qrcode");
    if (!scannerRef.current) return;
    const q = new Html5Qrcode(scannerRef.current.id);
    html5QrRef.current = q;
    try {
      await q.start({ facingMode: "environment" }, { fps: 8, qrbox: 240 }, onScan);
    } catch (e) {
      toast.error("Cannot start camera: " + (e?.message || e));
    }
  };

  const stopScanner = async () => {
    try {
      await html5QrRef.current?.stop();
      await html5QrRef.current?.clear();
    } catch (e) { /* ignore */ }
    html5QrRef.current = null;
  };

  const onScan = async (decodedText) => {
    let token = decodedText;
    try {
      const u = new URL(decodedText);
      const parts = u.pathname.split("/").filter(Boolean);
      token = parts[parts.length - 1];
    } catch (e) { /* not a URL, treat as raw token */ }
    await submitScan(token);
  };

  const submitScan = async (token) => {
    if (!token || token.length < 8) {
      toast.error("Invalid token");
      return;
    }
    try {
      const { data } = await api.post(`/rider/shipments/${active.id}/pickup-scan`, { scan_token: token });
      toast.success(`✓ ${data.package_label} (${data.scanned}/${data.expected})`);
      loadDetail();
    } catch (e) {
      toast.error(e.displayMessage || "Scan failed");
    }
  };

  // P4 — Rider package-measurement helpers now live in the dormant, non-rendered
  // `ParkedRiderMeasurementCard` component at the foot of this file (see memory/PARKED_MEASUREMENT_MODULE.md).


  const collectLetter = async () => {
    setCollecting(true);
    try {
      await api.post(`/booking/${active.id}/authority-letter/custody/collect`);
      toast.success("Authority Letter collection recorded");
      await loadDetail();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error((detail && detail.message) || e.displayMessage || "Could not record collection.");
      await loadDetail();
    } finally { setCollecting(false); }
  };

  const submitPickup = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rider/shipments/${active.id}/pickup-complete`, { physical_invoice_collected: physInvoice });
      toast.success(data?.idempotent ? "Pickup is already completed." : "Pickup complete — custody recorded");
      setActive(null);
      refresh();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const code = detail?.code;
      const msg = detail?.message;
      if (code === "SCAN_INCOMPLETE") {
        toast.error(msg || "Some packages are not scanned yet.");
        loadDetail();
      } else if (code === "PACKAGE_PHOTO_INCOMPLETE") {
        toast.error(msg || "Capture a pickup photo for each package.");
        loadDetail();
      } else if (code === "LOADED_VEHICLE_PHOTO_REQUIRED") {
        toast.error(msg || "Capture the loaded-vehicle photo before completing pickup.");
      } else if (code === "SHIPMENT_BLOCKED") {
        toast.error(msg || "An active Shipment Block prevents completing pickup.");
        loadDetail();
      } else if (code === "PICKUP_STAGE_INVALID") {
        toast.error(msg || "Pickup completion is not available at this stage.");
        loadDetail();
      } else if (code === "PICKUP_ALREADY_COMPLETED") {
        toast.error("Pickup is already completed.");
        setActive(null);
        refresh();
      } else if (code === "OPERATIONS_LOCKED") {
        toast.error("Physical operations are locked until the commercial steps are complete.");
      } else if (code === "AUTHORITY_LETTER_NOT_COLLECTED") {
        toast.error("Confirm collection of the original Authority Letter before completing pickup.");
        loadDetail();
      } else if (code === "AUTHORITY_LETTER_NOT_READY") {
        toast.error(msg || "The Sender Authority Letter is not ready yet.");
        loadDetail();
      } else if (code === "PHYSICAL_INVOICE_NOT_CONFIRMED") {
        toast.error(msg || "Confirm you have collected the physical invoice copy/copies.");
      } else {
        toast.error(e.displayMessage || "Pickup completion failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const scansDone = detail?.pickup_scan_progress?.scanned || 0;
  const scansTotal = detail?.pickup_scan_progress?.expected || 0;
  const photosCount = detail?.pickup_photo_count || 0;
  const pickupDone = !!detail?.pickup_completed_at;
  // P4 — measurement/scan stage + completeness (client aid only; the backend remains authoritative).
  const status = detail?.current_status;
  const scanStageOpen = (status === "SENDER_READY_CONFIRMED" || status === "SOURCE_VEHICLE_ASSIGNED") && !pickupDone;
  const completionStageOk = status === "SOURCE_VEHICLE_ASSIGNED";
  const packages = detail?.packages || [];
  const scannedIds = new Set(detail?.pickup_scan_progress?.scanned_package_ids || []);
  const allScanned = scansTotal > 0 && scansDone >= scansTotal;
  const unscannedLabels = packages.filter((p) => !scannedIds.has(p.id)).map((p) => p.public_label);

  // Active shipment selected
  if (active && detail) {
    return (
      <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="rider-shipment-detail">
        <button
          onClick={async () => { await stopScanner(); setActive(null); }}
          className="text-sm text-stone-500 mono mb-4"
          data-testid="rider-back-btn"
        >
          ← Back to shipments
        </button>

        <div className="mb-5">
          <h1 className="text-2xl mono font-medium" data-testid="rider-shipment-number">
            {detail.public_shipment_number}
          </h1>
          <div className="flex items-center gap-3 mt-2">
            <StatusPill status={detail.current_status} />
            {pickupDone && (
              <span className="text-emerald-600 text-xs mono inline-flex items-center gap-1">
                <CheckCircle weight="fill" /> Pickup complete
              </span>
            )}
          </div>
        </div>

        <CargoAlert
          restrictedGoods={detail.restricted_goods_declared}
          fragilePackages={(packages || []).filter((p) => p.fragile).map((p) => (p.public_label || "").replace(/\/\d+$/, ""))}
          className="mb-4"
          testid="rider-cargo-alert"
        />

        <div className="border border-stone-200 rounded-lg p-4 mb-4">
          <div className="text-xs mono text-stone-500 mb-1">PICKUP ADDRESS</div>
          <div className="text-sm">
            {detail.pickup_address?.line1}
            {detail.pickup_address?.line2 ? `, ${detail.pickup_address.line2}` : ""}
            <br />
            {detail.pickup_address?.city}, {detail.pickup_address?.state} {detail.pickup_address?.pincode}
          </div>
          {detail.load_assist_pickup && (
            <div className="text-xs mt-3 p-2 bg-blue-50 border border-blue-200 rounded" data-testid="rider-load-assist-pickup">
              <span className="font-semibold">{detail.load_assist_pickup.label}</span>.{" "}
              {detail.load_assist_pickup.arrangement_ready || detail.load_assist_pickup.confirmed
                ? "Load Assist arrangement ready."
                : "Load Assist arrangement to be confirmed by Control Tower."}
            </div>
          )}
        </div>

        {/* To-Do 5 — assigned Source vehicles (1..N), READ-ONLY. Each has its own loaded photo. */}
        {(detail.source_vehicles || []).length > 0 && (
          <div className="border border-stone-200 rounded-lg p-4 mb-4" data-testid="rider-source-vehicle">
            <div className="text-xs mono text-stone-500 mb-2">ASSIGNED SOURCE VEHICLES · READ-ONLY</div>
            <div className="space-y-1">
              {detail.source_vehicles.map((v, i) => (
                <div key={v.id} className="text-sm mono" data-testid={`rider-source-vehicle-number-${i}`}>
                  {v.vehicle_number || "—"} · Driver: {v.driver_name || "—"}
                  {v.driver_mobile ? ` · ${v.driver_mobile}` : ""}
                  {v.photo_covered ? <span className="ml-2 text-emerald-600">✓ loaded photo</span> : <span className="ml-2 text-amber-600">photo pending</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* M7 — original Authority Letter collection */}
        {!pickupDone && (
          <div className="border border-stone-200 rounded-lg p-4 mb-4" data-testid="rider-authority-letter-custody">
            <div className="text-xs mono text-stone-500 mb-2">ORIGINAL AUTHORITY LETTER</div>
            {custody && custody.rider_collection_completed ? (
              <div className="text-sm text-emerald-700 inline-flex items-center gap-1" data-testid="rider-letter-collected">
                <CheckCircle weight="fill" /> Original collected{custody.rider_collected_at ? ` · ${fmtDT(custody.rider_collected_at)}` : ""}
              </div>
            ) : custody && custody.current_letter_ready ? (
              <div className="space-y-3">
                <p className="text-sm text-stone-700" data-testid="rider-letter-affirmation">{custody.rider_affirmation}</p>
                <button
                  onClick={collectLetter}
                  disabled={collecting}
                  className="w-full bg-stone-900 text-white px-4 py-3 rounded text-sm mono font-medium disabled:opacity-50"
                  data-testid="rider-collect-letter-btn"
                >
                  {collecting ? "Recording…" : "Confirm original Authority Letter collected"}
                </button>
              </div>
            ) : (
              <div className="text-xs mono text-amber-600" data-testid="rider-letter-not-ready">
                Sender Authority Letter is not ready yet. Pickup cannot be completed until the original letter is available.
              </div>
            )}
          </div>
        )}

        {!pickupDone && status === "SENDER_READY_CONFIRMED" && (
          <div className="text-xs mono p-2 mb-4 bg-blue-50 border border-blue-200 rounded" data-testid="rider-stage-banner">
            You can scan and measure packages now. Pickup can be completed after the source vehicle is assigned.
          </div>
        )}
        {!pickupDone && !scanStageOpen && (
          <div className="text-xs mono p-2 mb-4 bg-stone-100 border border-stone-200 rounded" data-testid="rider-stage-locked-banner">
            New scanning and measurement are not available at this stage. Existing records are read-only.
          </div>
        )}

        <div className="border border-stone-200 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-xs mono text-stone-500">PACKAGE SCAN ({scansDone}/{scansTotal})</div>
          </div>
          <div className="flex gap-2 mb-3">
            <button
              onClick={() => html5QrRef.current ? stopScanner() : startScanner()}
              className="flex-1 bg-stone-900 text-white px-4 py-3 rounded text-sm mono inline-flex items-center justify-center gap-2"
              data-testid="rider-toggle-scanner-btn"
              disabled={!scanStageOpen}
            >
              <Camera /> {html5QrRef.current ? "Stop camera" : "Open camera"}
            </button>
          </div>
          <div id="rider-qr-region" ref={scannerRef} className="rounded overflow-hidden" />
          <div className="mt-3 flex gap-2">
            <input
              value={manualToken}
              onChange={(e) => setManualToken(e.target.value)}
              placeholder="Manual token fallback…"
              className="flex-1 border border-stone-300 rounded px-3 py-2 text-sm mono"
              data-testid="rider-manual-token-input"
              disabled={!scanStageOpen}
            />
            <button
              onClick={async () => { await submitScan(manualToken.trim()); setManualToken(""); }}
              className="bg-stone-200 text-stone-900 px-4 py-2 rounded text-sm mono"
              data-testid="rider-submit-manual-token-btn"
              disabled={!scanStageOpen}
            >
              <QrCode />
            </button>
          </div>
        </div>

        {/* PARKED (Module 9A) — Rider operational measurement UI lives as the dormant, non-rendered
            component `ParkedRiderMeasurementCard` at the foot of this file (executable source, kept in
            place for reactivation). It is intentionally NOT rendered here. See memory/PARKED_MEASUREMENT_MODULE.md */}

        <div className="border border-stone-200 rounded-lg p-4 mb-4">
          <div className="text-xs mono text-stone-500 mb-2">PACKAGE PHOTOS · one per package</div>
          <div className="space-y-3">
            {packages.map((p) => (
              <div key={p.id} className="flex items-center gap-3" data-testid={`rider-pkg-photo-row-${p.id}`}>
                <span className="text-sm mono flex-1" data-testid={`rider-pkg-label-${p.id}`}>
                  {(p.public_label || "").replace(/\/\d+$/, "")}
                  {p.added_after_main && <span className="ml-1 text-[10px] font-semibold text-amber-600" data-testid={`rider-pkg-added-${p.id}`}>Added</span>}
                </span>
                <CameraEvidenceCapture
                  shipmentId={active.id}
                  docType="PICKUP_PHOTO"
                  packageId={p.id}
                  label={`Photo · ${p.public_label}`}
                  stage="PICKUP"
                  disabled={pickupDone}
                  onCaptured={loadDetail}
                  buttonClassName="border border-dashed border-stone-300 rounded px-3 py-2 text-xs mono inline-flex items-center gap-2 disabled:opacity-50"
                  buttonLabel="Take photo"
                />
              </div>
            ))}
          </div>
        </div>

        <div className="border border-stone-200 rounded-lg p-4 mb-4">
          <div className="text-xs mono text-stone-500 mb-2">LOADED VEHICLE PHOTOS · one per vehicle</div>
          <div className="text-xs mono text-stone-400 mb-3">Capture one photo of the shipment loaded into each assigned pickup vehicle.</div>
          <div className="space-y-3">
            {(detail.source_vehicles || []).map((v) => (
              <div key={v.id} className="flex items-center gap-3" data-testid={`rider-loaded-photo-row-${v.id}`}>
                <span className="text-sm mono flex-1">
                  {v.vehicle_number || "—"}
                  {v.photo_covered && <span className="ml-2 text-emerald-600 text-xs">✓ captured</span>}
                </span>
                <CameraEvidenceCapture
                  shipmentId={active.id}
                  docType="PICKUP_VEHICLE_LOADED"
                  roadLegId={v.id}
                  label={`Loaded vehicle photo · ${v.vehicle_number}`}
                  stage="PICKUP"
                  // AIR12 FD-2 — same window as backend VEHICLE_PHOTO_WINDOWS: SENDER_READY_CONFIRMED /
                  // SOURCE_VEHICLE_ASSIGNED and Pickup Complete not recorded (scanStageOpen).
                  disabled={!scanStageOpen}
                  onCaptured={loadDetail}
                  buttonClassName="border border-dashed border-stone-300 rounded px-3 py-2 text-xs mono inline-flex items-center gap-2 disabled:opacity-50"
                  buttonLabel="Take loaded-vehicle photo"
                />
              </div>
            ))}
            {(detail.source_vehicles || []).length === 0 && (
              <div className="text-xs mono text-amber-600">Loaded-vehicle photos can be captured once source vehicles are assigned.</div>
            )}
          </div>
        </div>

        {activeBlock && !pickupDone && (
          <div className="p-3 mb-3 rounded border border-red-300 bg-red-50 text-sm" data-testid="rider-active-block-banner">
            <div className="font-semibold text-red-700">Shipment Block active — pickup cannot be completed</div>
            <div className="text-xs text-red-600 mt-1">Reason: {activeBlock.reason}</div>
            <div className="text-xs text-stone-500 mt-1">You may still record scans and photos. Completion is blocked until a City Controller resolves the block.</div>
          </div>
        )}

        {!pickupDone && (
          <div className="text-xs mono p-3 mb-3 border border-stone-200 rounded" data-testid="rider-measure-progress">
            <div>Scanned {scansDone} of {scansTotal}{unscannedLabels.length > 0 ? ` · pending: ${unscannedLabels.join(", ")}` : ""}</div>
            {!completionStageOk && (
              <div className="text-amber-600 mt-1">Pickup can be completed after the source vehicle is assigned.</div>
            )}
          </div>
        )}

        {!pickupDone && (
          <label
            className="flex items-start gap-2 text-sm p-3 mb-3 border border-stone-200 rounded cursor-pointer"
            data-testid="rider-physical-invoice-confirm"
          >
            <input
              type="checkbox"
              className="mt-0.5"
              checked={physInvoice}
              onChange={(e) => setPhysInvoice(e.target.checked)}
              data-testid="rider-physical-invoice-checkbox"
            />
            <span>
              <span className="font-medium">Physical invoice copy/copies collected</span>
              <span className="block text-xs text-stone-500 mt-0.5">
                Confirm you have the physical invoice copy/copies travelling with this shipment. (Reminder only — no counting or checking required.)
              </span>
            </span>
          </label>
        )}

        {!pickupDone && (
          <button
            onClick={submitPickup}
            disabled={submitting || !allScanned || !completionStageOk || !!activeBlock || !(custody && custody.rider_collection_completed) || !physInvoice}
            className="w-full bg-emerald-600 text-white px-4 py-3 rounded text-sm mono font-medium disabled:opacity-50"
            data-testid="rider-submit-pickup-complete-btn"
          >
            {submitting ? "Submitting…" : "Confirm pickup complete"}
          </button>
        )}
      </div>
    );
  }

  // List view
  return (
    <div className="max-w-2xl mx-auto p-4 sm:p-6" data-testid="rider-home">
      <header className="mb-4">
        <div className="text-xs mono text-stone-500">RIDER · {profile.public_staff_id || "—"}</div>
        <h1 className="text-2xl mono font-medium">Pickup queue</h1>
      </header>

      {shipments.length === 0 ? (
        <div className="text-sm mono text-stone-500 py-12 text-center" data-testid="rider-empty-state">
          No shipments assigned to you right now.
        </div>
      ) : (
        <div className="space-y-3">
          {shipments.map((s) => (
            <button
              key={s.id}
              onClick={() => setActive(s)}
              className="w-full text-left border border-stone-200 rounded-lg p-4 hover:border-stone-900 transition"
              data-testid={`rider-shipment-row-${s.public_shipment_number}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="mono text-sm font-medium">{s.public_shipment_number}</div>
                  <div className="text-xs mono text-stone-500 mt-1">
                    {s.source_airport} → {s.destination_airport}
                    {s.pickup_address_city ? ` · ${s.pickup_address_city}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {s.pickup_completed_at && (
                    <span className="text-emerald-600 text-xs mono">✓ Done</span>
                  )}
                  <StatusPill status={s.current_status} />
                </div>
              </div>
              {s.pickup_rider_assigned_at && (
                <div className="text-xs mono text-stone-400 mt-2">
                  assigned {fmtDT(s.pickup_rider_assigned_at)}
                </div>
              )}
            </button>
          ))}
        </div>
      )}

      <div className="mt-8">
        <HandledHistoryPanel testidPrefix="rider-handled-history" />
      </div>

      <div className="mt-8 text-center">
        <Link to="/" className="text-xs mono text-stone-500 underline">Sign out / Switch</Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// PARKED (Module 9A) — Rider operational package-measurement card.
// Executable, self-contained source kept IN PLACE for future reactivation. It is
// intentionally NOT rendered anywhere in the active flow: Air12's authoritative
// weight is now the IndiGo AWB final chargeable weight, so on-pickup measurement
// is dormant. Do NOT delete. To reactivate, render inside RiderHome, e.g.:
//   <ParkedRiderMeasurementCard shipmentId={active.id} packages={packages}
//     scannedIds={scannedIds} pickupDone={pickupDone} scanStageOpen={scanStageOpen}
//     onSaved={loadDetail} />
// and restore the pickup-completion `allMeasured` gate + the "Measured X of Y"
// progress line. See memory/PARKED_MEASUREMENT_MODULE.md
// ─────────────────────────────────────────────────────────────────────────────
export function ParkedRiderMeasurementCard({ shipmentId, packages, scannedIds, pickupDone, scanStageOpen, onSaved }) {
  const [measureDraft, setMeasureDraft] = useState({});
  const [savingMeasureId, setSavingMeasureId] = useState(null);
  const measureStageOpen = scanStageOpen;

  const draftFor = (p) => measureDraft[p.id] ?? {
    length: p.rider_measured_length_cm != null ? String(p.rider_measured_length_cm) : "",
    width: p.rider_measured_width_cm != null ? String(p.rider_measured_width_cm) : "",
    height: p.rider_measured_height_cm != null ? String(p.rider_measured_height_cm) : "",
    type: p.rider_observed_package_type || "",
    note: p.rider_measurement_note || "",
  };

  const setDraftField = (p, field, value) =>
    setMeasureDraft((prev) => {
      const cur = prev[p.id] ?? {
        length: p.rider_measured_length_cm != null ? String(p.rider_measured_length_cm) : "",
        width: p.rider_measured_width_cm != null ? String(p.rider_measured_width_cm) : "",
        height: p.rider_measured_height_cm != null ? String(p.rider_measured_height_cm) : "",
        type: p.rider_observed_package_type || "",
        note: p.rider_measurement_note || "",
      };
      return { ...prev, [p.id]: { ...cur, [field]: value } };
    });

  const measureErrorCopy = (e) => {
    const detail = e?.response?.data?.detail;
    const map = {
      SCAN_REQUIRED_BEFORE_MEASURE: "Scan this package at pickup before recording its measurement.",
      MEASUREMENT_LOCKED: "This package measurement is locked because pickup has been completed.",
      PICKUP_ALREADY_COMPLETED: "Pickup is already completed; measurements are locked.",
      PICKUP_STAGE_INVALID: "This action is not available at the current shipment stage.",
      INVALID_DIMENSIONS: "Enter valid positive dimensions within the supported range.",
      INVALID_PACKAGE_TYPE: "Select a valid observed package type.",
      PACKAGE_NOT_IN_SHIPMENT: "This package is not part of the shipment.",
    };
    return map[detail?.code] || detail?.message || e.displayMessage || "Could not save measurement.";
  };

  const submitMeasure = async (p) => {
    const d = draftFor(p);
    const len = parseFloat(d.length), wid = parseFloat(d.width), hgt = parseFloat(d.height);
    if (!(len > 0) || !(wid > 0) || !(hgt > 0)) return toast.error("Enter valid positive dimensions.");
    if (!d.type) return toast.error("Select an observed package type.");
    setSavingMeasureId(p.id);
    try {
      const body = { package_id: p.id, length_cm: len, width_cm: wid, height_cm: hgt, observed_package_type: d.type };
      if (d.note && d.note.trim()) body.note = d.note.trim();
      const { data } = await api.post(`/rider/shipments/${shipmentId}/pickup-measure`, body);
      if (data.idempotent) toast.info(`No measurement changes for ${p.public_label}.`);
      else toast.success(`✓ ${p.public_label} ${data.action === "revised" ? "measurement updated" : "measured"}`);
      setMeasureDraft((prev) => { const n = { ...prev }; delete n[p.id]; return n; });
      onSaved && onSaved();
    } catch (e) {
      toast.error(measureErrorCopy(e));
    } finally {
      setSavingMeasureId(null);
    }
  };

  return (
    <div className="border border-stone-200 rounded-lg p-4 mb-4" data-testid="rider-measure-card">
      <div className="text-xs mono text-stone-500 mb-1">PACKAGE MEASUREMENT</div>
      <div className="text-xs mono text-stone-400 mb-3 leading-relaxed">
        Record the observed package dimensions. Do not enter weight.<br />
        Measurements are rounded up to the next whole centimetre.
      </div>
      {packages.length === 0 ? (
        <div className="text-xs mono text-stone-400">No packages on this shipment.</div>
      ) : (
        <div className="space-y-3">
          {packages.map((p) => {
            const scanned = scannedIds.has(p.id);
            const locked = pickupDone || p.rider_measurement_locked;
            const editable = scanned && !locked && measureStageOpen;
            const dr = draftFor(p);
            const saving = savingMeasureId === p.id;
            const complete = dr.length && dr.width && dr.height && dr.type;
            let stateLabel, stateCls;
            if (locked) { stateLabel = "Locked"; stateCls = "text-stone-400"; }
            else if (!scanned) { stateLabel = "Not scanned"; stateCls = "text-stone-400"; }
            else if (p.rider_measured) { stateLabel = "Measured"; stateCls = "text-emerald-600"; }
            else { stateLabel = "Measurement required"; stateCls = "text-amber-600"; }
            return (
              <div key={p.id} className="border border-stone-200 rounded p-3" data-testid={`rider-measure-row-${p.public_label}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="mono text-sm font-medium">{p.public_label}</span>
                  <span className={`text-[11px] mono inline-flex items-center gap-1 ${stateCls}`} data-testid={`rider-measure-status-${p.public_label}`}>
                    {p.rider_measured && !locked && <CheckCircle weight="fill" />}{stateLabel}
                  </span>
                </div>
                <div className="text-[11px] mono text-stone-500 mb-2">
                  Declared: {p.length_cm} × {p.width_cm} × {p.height_cm} cm · {p.package_type_label}
                </div>
                {!scanned && !locked && (
                  <div className="text-[11px] mono text-amber-600 mb-2" data-testid={`rider-measure-scanhint-${p.public_label}`}>
                    Scan this package before recording its measurement.
                  </div>
                )}
                {locked && p.rider_measured ? (
                  <div className="text-xs mono text-stone-600" data-testid={`rider-measure-locked-${p.public_label}`}>
                    Measured at pickup: {p.rider_measured_length_cm} × {p.rider_measured_width_cm} × {p.rider_measured_height_cm} cm · {p.rider_observed_package_type_label}
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-2 mb-2">
                      {[["length", "L (cm)"], ["width", "W (cm)"], ["height", "H (cm)"]].map(([f, ph]) => (
                        <input
                          key={f}
                          type="number" min="0" step="any" inputMode="decimal"
                          value={dr[f]}
                          placeholder={ph}
                          disabled={!editable || saving}
                          onChange={(e) => setDraftField(p, f, e.target.value)}
                          className="border border-stone-300 rounded px-2 py-2 text-sm mono disabled:bg-stone-50 disabled:text-stone-400"
                          data-testid={`rider-measure-${f}-${p.public_label}`}
                        />
                      ))}
                    </div>
                    <select
                      value={dr.type}
                      disabled={!editable || saving}
                      onChange={(e) => setDraftField(p, "type", e.target.value)}
                      className="w-full border border-stone-300 rounded px-2 py-2 text-sm mono mb-2 disabled:bg-stone-50 disabled:text-stone-400"
                      data-testid={`rider-measure-type-${p.public_label}`}
                    >
                      <option value="">Select observed package type</option>
                      {OBSERVED_PACKAGE_TYPES.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                    <input
                      value={dr.note}
                      disabled={!editable || saving}
                      onChange={(e) => setDraftField(p, "note", e.target.value)}
                      placeholder="Note (optional)"
                      className="w-full border border-stone-300 rounded px-2 py-2 text-sm mono mb-2 disabled:bg-stone-50 disabled:text-stone-400"
                      data-testid={`rider-measure-note-${p.public_label}`}
                    />
                    <button
                      onClick={() => submitMeasure(p)}
                      disabled={!editable || saving || !complete}
                      className="w-full bg-stone-900 text-white px-4 py-2 rounded text-sm mono disabled:opacity-40"
                      data-testid={`rider-measure-save-${p.public_label}`}
                    >
                      {saving ? "Saving…" : p.rider_measured ? "Update measurement" : "Save measurement"}
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
